import { ComponentProps, FC, forwardRef } from 'react'

import { clsx } from 'clsx'

import type { BadgeVariant } from '../Badge/BadgeContext.js'
import { Badge } from '../Badge/index.js'
import { Icon } from '../Icon/index.js'
import type { IconComponentType } from '../Icon/index.js'
import { SelectorIcon, XIcon } from '../icons/index.js'
import { HTMLTribeProps } from '../types/index.js'
import { useFloatingContext } from '../utils/floating/index.js'
import { useInputColors } from '../utils/styles/index.js'
import { useMultiselect } from './MultiselectContext.js'

export type MultiselectButtonProps = ComponentProps<'button'> & {
  hideInput?: boolean
  dense?: boolean
  leadingIcon?: IconComponentType
}

export const MultiselectButton = forwardRef<
  HTMLButtonElement,
  MultiselectButtonProps
>((props, ref) => {
  const {
    children,
    className,
    placeholder,
    dense = false,
    leadingIcon,
    hideInput = false,
    ...rest
  } = props
  const {
    inputValue,
    searchable,
    disabled,
    invalid,
    isOpen,
    getComboboxProps,
    getInputProps,
    getDropdownProps,
    getToggleButtonProps,
    setSelectedItems,
    selectedItems,
    addSelectedItem,
    reset,
    onCreateItem,
  } = useMultiselect()
  const { reference } = useFloatingContext()
  const {
    backgroundClsx,
    borderClsx,
    invalidClsx,
    disabledClsx,
    iconClsx,
    placeholderClsx,
    fontClsx,
    focusClsx,
  } = useInputColors({
    invalid,
    disabled,
  })

  const onKeyDown = event => {
    if (event.key === 'Backspace' && !inputValue) {
      setSelectedItems(selectedItems.slice(0, selectedItems.length - 1))
      reset()
    }
    if (event.key === 'Escape') {
      event.stopPropagation()
    }
  }

  const onPaste = event => {
    // if paste detected and current input is empty, create items without confirmation
    if (!onCreateItem || !!inputValue) {
      return
    }
    const content: string = event?.clipboardData?.getData('Text')
    if (!content) {
      return
    }

    const items = content
      .split(/[\n,]+/)
      .map(it => it.trim())
      .filter(it => !!it)
    const uniqueItems = [...new Set(items)]

    uniqueItems?.forEach(item => {
      if (item && !selectedItems.includes(item)) {
        addSelectedItem(item)
        onCreateItem(item)
      }
    })
    event.preventDefault()
  }

  return (
    <div ref={reference}>
      <div
        className={clsx(
          'block w-full rounded-md group relative',
          'py-2 text-start cursor-default',
          [
            'appearance-none',
            'text-base',
            fontClsx,
            backgroundClsx,
            !dense && borderClsx,
            !dense && focusClsx,
            invalid && invalidClsx,
            disabled && disabledClsx,
          ],
          !dense && 'ps-3 pe-10',
          className,
        )}
        {...getComboboxProps(getToggleButtonProps({ disabled, ref, ...rest }))}
      >
        <span className="block truncate">
          <div className="flex flex-wrap -ms-2 -mt-2">
            {leadingIcon && (
              <Icon
                className={clsx('ms-2 mt-2', 'h-5 w-5', !!children && 'me-1')}
              >
                {leadingIcon}
              </Icon>
            )}
            {children}
            <input
              className={clsx(
                'focus:outline-none bg-transparent flex-1 ms-2 mt-2',
                'text-base',
                fontClsx,
                placeholderClsx,
                !searchable && ' caret-transparent',
                hideInput && !isOpen && 'w-0 h-0 invisible',
                hideInput && isOpen && !searchable && 'w-0 h-0 invisible',
              )}
              {...getInputProps({
                ...getDropdownProps({
                  preventKeyAction: isOpen,
                }),
                placeholder,
                disabled,
                onKeyDown,
                onPaste,
              })}
              onClick={e => {
                if (hideInput) {
                  e.stopPropagation()
                }
              }}
              readOnly={!searchable}
            />
          </div>
        </span>
        {!dense && (
          <span
            className="absolute inset-y-0 end-0 flex items-center pe-2 pointer-events-none"
            aria-label="toggle menu"
          >
            <SelectorIcon
              className={clsx('w-5 h-5', iconClsx)}
              aria-hidden="true"
            />
          </span>
        )}
      </div>
    </div>
  )
})

export type MultiselectSelectedItemProps = HTMLTribeProps<'div'> & {
  value: unknown
  index: number
  badgeVariant?: BadgeVariant
}

export const MultiselectSelectedItem: FC<
  MultiselectSelectedItemProps
> = props => {
  const {
    value,
    badgeVariant = 'secondary',
    index,
    children,
    className,
    ...rest
  } = props

  const { removeSelectedItem, getSelectedItemProps, disabled } =
    useMultiselect()

  return (
    <Badge
      variant={badgeVariant}
      trailingIcon={
        <Badge.Button
          onClick={e => {
            e.stopPropagation()
            removeSelectedItem(value)
          }}
          disabled={disabled}
        >
          <span className="sr-only">Remove option</span>
          <Icon className="h-2 w-2">
            <XIcon />
          </Icon>
        </Badge.Button>
      }
      className={clsx('ms-2 mt-2 truncate', className)}
      {...rest}
      {...getSelectedItemProps({ selectedItem: value, index })}
    >
      <div className="truncate">{children}</div>
    </Badge>
  )
}
